/**
 * 🎯 BREAKPOINTS UNIFIÉS — Universal Cold Plannit® V3.7
 * 
 * Configuration UNIQUE centralisée pour tous les media queries responsive
 * Remplace les 3 configurations incohérentes précédentes :
 * - breakpoints.js (mobile: 480, tablet: 1024, desktop: 1281)
 * - responsive.js (MOBILE: 768, TABLET: 1024, DESKTOP: 1280)
 * - theme.js (mobile: '480px', tablet: '1024px', desktop: '1280px')
 * 
 * ✅ 01 MARS 2026 : Configuration standardisée basée sur devices réels
 * 
 * @see Documentation complète : 📊 AUDIT_RESPONSIVE_COMPLET_01MARS2026.md
 */

// ============================================
// 📱 BREAKPOINTS VALEURS (en pixels)
// ============================================

export const UNIFIED_BREAKPOINTS = {
  // Mobile Portrait (320px - 480px)
  mobileXS: 320,        // iPhone SE (plus petit device supporté)
  mobileS: 375,         // iPhone 12/13 Mini
  mobileM: 390,         // iPhone 14 Pro
  mobileL: 414,         // iPhone Plus / Android large
  mobile: 480,          // Limite générale mobile portrait
  
  // Mobile Landscape (481px - 767px)
  mobileLandscape: 481,
  mobileLandscapeMax: 767,
  
  // Tablette Portrait (768px - 1023px)
  tablet: 768,
  tabletMax: 1023,
  
  // Tablette Landscape / Laptop (1024px - 1279px)
  tabletLandscape: 1024,
  laptop: 1280,
  
  // Desktop (1280px+)
  desktop: 1440,
  desktopLarge: 1920,
  ultraWide: 2560,
};

// ============================================
// 📐 MEDIA QUERIES STRINGS (prêtes à l'emploi)
// ============================================

export const MEDIA_QUERIES = {
  // ===== MOBILE PORTRAIT =====
  
  // iPhone SE (320px - 374px)
  mobileXS: `@media (max-width: 374px)`,
  mobileXSOnly: `@media (min-width: 320px) and (max-width: 374px)`,
  
  // iPhone 12/13 Mini (375px - 389px)
  mobileS: `@media (min-width: 375px) and (max-width: 389px)`,
  
  // iPhone 14 Pro (390px - 414px)
  mobileM: `@media (min-width: 390px) and (max-width: 414px)`,
  
  // iPhone Plus / Android Large (415px - 480px)
  mobileL: `@media (min-width: 415px) and (max-width: 480px)`,
  
  // Tous mobiles portrait (≤ 480px)
  mobile: `@media (max-width: ${UNIFIED_BREAKPOINTS.mobile}px)`,
  mobileOnly: `@media (max-width: ${UNIFIED_BREAKPOINTS.mobile}px) and (orientation: portrait)`,
  
  // ===== MOBILE LANDSCAPE =====
  
  mobileLandscape: `@media (min-width: ${UNIFIED_BREAKPOINTS.mobileLandscape}px) and (max-width: ${UNIFIED_BREAKPOINTS.mobileLandscapeMax}px) and (orientation: landscape)`,
  mobileLandscapeOnly: `@media (max-width: ${UNIFIED_BREAKPOINTS.mobileLandscapeMax}px) and (orientation: landscape)`,
  
  // ===== TABLETTE =====
  
  // Tablette Portrait (768px - 1023px)
  tabletPortrait: `@media (min-width: ${UNIFIED_BREAKPOINTS.tablet}px) and (max-width: ${UNIFIED_BREAKPOINTS.tabletMax}px) and (orientation: portrait)`,
  
  // Tablette Landscape (1024px - 1279px)
  tabletLandscape: `@media (min-width: ${UNIFIED_BREAKPOINTS.tabletLandscape}px) and (max-width: ${UNIFIED_BREAKPOINTS.laptop - 1}px) and (orientation: landscape)`,
  
  // Toutes tablettes (768px - 1279px)
  tablet: `@media (min-width: ${UNIFIED_BREAKPOINTS.tablet}px) and (max-width: ${UNIFIED_BREAKPOINTS.laptop - 1}px)`,
  
  // ===== DESKTOP =====
  
  // Laptop (1280px - 1439px)
  laptop: `@media (min-width: ${UNIFIED_BREAKPOINTS.laptop}px) and (max-width: ${UNIFIED_BREAKPOINTS.desktop - 1}px)`,
  
  // Desktop (1440px - 1919px)
  desktop: `@media (min-width: ${UNIFIED_BREAKPOINTS.desktop}px) and (max-width: ${UNIFIED_BREAKPOINTS.desktopLarge - 1}px)`,
  
  // Desktop Large (1920px - 2559px)
  desktopLarge: `@media (min-width: ${UNIFIED_BREAKPOINTS.desktopLarge}px) and (max-width: ${UNIFIED_BREAKPOINTS.ultraWide - 1}px)`,
  
  // Ultra Wide (≥ 2560px)
  ultraWide: `@media (min-width: ${UNIFIED_BREAKPOINTS.ultraWide}px)`,
  
  // ===== UTILITAIRES COMBINÉS =====
  
  // Mobile + Tablette (≤ 1279px)
  mobileAndTablet: `@media (max-width: ${UNIFIED_BREAKPOINTS.laptop - 1}px)`,
  
  // Tablette + Desktop (≥ 768px)
  tabletAndDesktop: `@media (min-width: ${UNIFIED_BREAKPOINTS.tablet}px)`,
  
  // Desktop uniquement (≥ 1280px)
  desktopOnly: `@media (min-width: ${UNIFIED_BREAKPOINTS.laptop}px)`,
  
  // ===== ORIENTATION =====
  
  portrait: `@media (orientation: portrait)`,
  landscape: `@media (orientation: landscape)`,
};

// ============================================
// 🎨 DESIGN TOKENS RESPONSIVE
// ============================================

/**
 * Fonction utilitaire pour créer des valeurs responsive
 * @param {string|number} mobile - Valeur mobile (≤ 480px)
 * @param {string|number} tablet - Valeur tablette (768px - 1279px)
 * @param {string|number} desktop - Valeur desktop (≥ 1280px)
 * @returns {object} Objet avec breakpoints
 */
export const responsive = (mobile, tablet = mobile, desktop = tablet) => ({
  mobile,
  tablet,
  desktop,
});

/**
 * 📏 SPACING TOKENS RESPONSIVE
 * Base 8px mobile → 12px tablet → 16px desktop
 */
export const SPACING = {
  xs: responsive('2px', '3px', '4px'),
  sm: responsive('4px', '6px', '8px'),
  md: responsive('8px', '12px', '16px'),
  base: responsive('12px', '16px', '20px'),
  lg: responsive('16px', '24px', '32px'),
  xl: responsive('24px', '32px', '48px'),
  '2xl': responsive('32px', '48px', '64px'),
  '3xl': responsive('48px', '64px', '96px'),
};

/**
 * 📝 FONT SIZE TOKENS RESPONSIVE
 * Scale typographique mobile-first
 */
export const FONT_SIZE = {
  // Body
  xs: responsive('0.6875rem', '0.75rem', '0.8125rem'),      // 11px → 12px → 13px
  sm: responsive('0.75rem', '0.8125rem', '0.875rem'),       // 12px → 13px → 14px
  base: responsive('0.875rem', '0.9375rem', '1rem'),        // 14px → 15px → 16px
  md: responsive('1rem', '1.0625rem', '1.125rem'),          // 16px → 17px → 18px
  lg: responsive('1.125rem', '1.25rem', '1.375rem'),        // 18px → 20px → 22px
  
  // Headings
  h6: responsive('0.875rem', '1rem', '1.125rem'),           // 14px → 16px → 18px
  h5: responsive('1rem', '1.125rem', '1.25rem'),            // 16px → 18px → 20px
  h4: responsive('1.125rem', '1.25rem', '1.5rem'),          // 18px → 20px → 24px
  h3: responsive('1.25rem', '1.5rem', '1.75rem'),           // 20px → 24px → 28px
  h2: responsive('1.5rem', '1.875rem', '2.25rem'),          // 24px → 30px → 36px
  h1: responsive('1.875rem', '2.5rem', '3rem'),             // 30px → 40px → 48px
  
  // Display
  display: responsive('2.5rem', '3.5rem', '4.5rem'),        // 40px → 56px → 72px
};

/**
 * 🎯 TOUCH TARGET SIZES
 * Conformes standards iOS (44px) / Android (48px) / WCAG AAA (44px)
 */
export const TOUCH_TARGET = {
  mobile: '44px',       // iOS minimum
  tablet: '40px',       // Tablette
  desktop: '36px',      // Desktop (souris précise)
};

/**
 * 📦 CONTAINER MAX-WIDTH
 * Largeurs maximales pour éviter distorsion ultra-wide
 */
export const CONTAINER_MAX_WIDTH = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1536px',
  '3xl': '1920px',
  full: '2400px',       // Max absolu pour ultra-wide
};

/**
 * 🔲 BORDER RADIUS RESPONSIVE
 */
export const BORDER_RADIUS = {
  sm: responsive('4px', '6px', '8px'),
  base: responsive('6px', '8px', '12px'),
  lg: responsive('8px', '12px', '16px'),
  xl: responsive('12px', '16px', '24px'),
  full: '9999px',
};

/**
 * 📊 Z-INDEX SCALE
 * Maintenabilité stack order
 */
export const Z_INDEX = {
  base: 1,
  dropdown: 1000,
  sticky: 1010,
  fixed: 1020,
  modalBackdrop: 1030,
  modal: 1040,
  popover: 1050,
  tooltip: 1060,
  notification: 1070,
};

// ============================================
// 🛠️ UTILITAIRES HELPERS
// ============================================

/**
 * Génère media query pour screen width minimum
 * @param {number} minWidth - Largeur minimum en pixels
 * @returns {string} Media query
 */
export const minWidth = (minWidth) => `@media (min-width: ${minWidth}px)`;

/**
 * Génère media query pour screen width maximum
 * @param {number} maxWidth - Largeur maximum en pixels
 * @returns {string} Media query
 */
export const maxWidth = (maxWidth) => `@media (max-width: ${maxWidth}px)`;

/**
 * Génère media query pour range width
 * @param {number} min - Largeur minimum
 * @param {number} max - Largeur maximum
 * @returns {string} Media query
 */
export const between = (min, max) => 
  `@media (min-width: ${min}px) and (max-width: ${max}px)`;

/**
 * Applique valeur responsive selon breakpoint
 * Usage: ${applyResponsive(FONT_SIZE.h1, 'font-size')}
 * 
 * @param {object} values - Objet { mobile, tablet, desktop }
 * @param {string} property - Propriété CSS
 * @returns {string} CSS avec media queries
 */
export const applyResponsive = (values, property) => `
  ${property}: ${values.mobile};
  
  ${MEDIA_QUERIES.tablet} {
    ${property}: ${values.tablet};
  }
  
  ${MEDIA_QUERIES.desktopOnly} {
    ${property}: ${values.desktop};
  }
`;

/**
 * 📱 Hook React détection breakpoint actuel
 * Usage: const { isMobile, isTablet, isDesktop } = useBreakpoint();
 * 
 * @returns {string} Breakpoint actuel
 */
export const getBreakpoint = () => {
  if (typeof window === 'undefined') return 'desktop';
  
  const width = window.innerWidth;
  
  if (width <= UNIFIED_BREAKPOINTS.mobile) return 'mobile';
  if (width <= UNIFIED_BREAKPOINTS.mobileLandscapeMax) return 'mobileLandscape';
  if (width <= UNIFIED_BREAKPOINTS.tabletMax) return 'tablet';
  if (width < UNIFIED_BREAKPOINTS.laptop) return 'tabletLandscape';
  if (width < UNIFIED_BREAKPOINTS.desktop) return 'laptop';
  if (width < UNIFIED_BREAKPOINTS.desktopLarge) return 'desktop';
  if (width < UNIFIED_BREAKPOINTS.ultraWide) return 'desktopLarge';
  return 'ultraWide';
};

/**
 * ✅ Tests unitaires breakpoints
 */
export const isMobile = () => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth <= UNIFIED_BREAKPOINTS.mobile;
};

export const isTablet = () => {
  if (typeof window === 'undefined') return false;
  const w = window.innerWidth;
  return w > UNIFIED_BREAKPOINTS.mobile && w < UNIFIED_BREAKPOINTS.laptop;
};

export const isDesktop = () => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth >= UNIFIED_BREAKPOINTS.laptop;
};

// ============================================
// 📤 EXPORTS GROUPÉS
// ============================================

const UnifiedBreakpoints = {
  UNIFIED_BREAKPOINTS,
  MEDIA_QUERIES,
  SPACING,
  FONT_SIZE,
  TOUCH_TARGET,
  CONTAINER_MAX_WIDTH,
  BORDER_RADIUS,
  Z_INDEX,
  responsive,
  minWidth,
  maxWidth,
  between,
  applyResponsive,
  getBreakpoint,
  isMobile,
  isTablet,
  isDesktop,
};
export default UnifiedBreakpoints;
